import { DateTime } from 'luxon';
import { lowBatteryPercent } from '../constants';

/** "HUB-1, HUB-2, HUB-3 and 4 more" */
const listOf = (names, shown = 3) => {
  if (names.length <= shown) return names.join(', ');
  return names.slice(0, shown).join(', ') + ' and ' + (names.length - shown) + ' more';
};

/** "41m", "2h", "3d" since a time, or null when there is none. */
export const ageOf = (since) => {
  if (since == null) return null;
  const minutes = Math.max(0, Math.floor(DateTime.now().diff(since, 'minutes').minutes));
  if (minutes < 60) return minutes + 'm';
  if (minutes < 60 * 24) return Math.floor(minutes / 60) + 'h';
  return Math.floor(minutes / (60 * 24)) + 'd';
};

/**
 * What needs attention on campus right now, worst first: offline hubs, badges
 * below the low battery threshold, staff without a badge. The one list behind
 * the Overview's panel, the header bell and the Overview count in the drawer
 * and pills.
 *
 * Each item's id names the devices or people in it, so dismissing an item
 * lasts only until what it is about changes: a second hub going offline
 * brings the hubs item back.
 *
 * @param {{ hubs: import('.').Hub[] | null, staff: import('.').StaffMember[] | null }} fleet
 * @returns {{ id: string, tone: 'bad' | 'warn', title: string, meta: string | null, since: DateTime | null, link: string }[]}
 */
export function attentionItems({ hubs, staff }) {
  const items = [];

  const offline = (hubs ?? []).filter((hub) => !hub.getIsConnected());
  if (offline.length > 0) {
    const serials = offline.map((hub) => hub.getSerialNumber()).sort();
    const seen = offline.map((hub) => hub.getLastSeenTimestamp()).filter(Boolean);
    items.push({
      id: 'hubs:' + serials.join(','),
      tone: 'bad',
      title: offline.length + (offline.length === 1 ? ' hub not connected' : ' hubs not connected'),
      meta: listOf(serials),
      // Since the longest-silent hub was last heard from.
      since: seen.length > 0 ? DateTime.min(...seen) : null,
      link: '/dashboard/devices?tab=Hubs' + (offline.length === 1 ? '&select=' + encodeURIComponent(serials[0]) : '')
    });
  }

  const badges = (staff ?? []).map((s) => s.getBadge()).filter(Boolean);
  const low = badges
    .filter((b) => b.getBatteryLevel() != null && b.getBatteryLevel() < lowBatteryPercent())
    .sort((a, b) => a.getBatteryLevel() - b.getBatteryLevel());
  if (low.length > 0) {
    const serials = low.map((b) => String(b.getSerialNumber()));
    items.push({
      id: 'badges:' + [...serials].sort().join(','),
      tone: 'warn',
      title: low.length + (low.length === 1 ? ' badge below ' : ' badges below ') + lowBatteryPercent() + '% power',
      meta: listOf(low.map((b) => b.getSerialNumber() + ' (' + Math.floor(b.getBatteryLevel()) + '%)')),
      since: null,
      link: '/dashboard/devices?tab=Badges' + (low.length === 1 ? '&select=' + encodeURIComponent(serials[0]) : '')
    });
  }

  const without = (staff ?? []).filter((s) => s.getBadge() == null);
  if (without.length > 0) {
    const name = (s) => {
      const type = s.getStaffType()?.getName();
      return (s.getFirstName()?.[0] ? s.getFirstName()[0] + '. ' : '') + (s.getLastName() ?? '') + (type ? ' · ' + type : '');
    };
    items.push({
      id: 'unassigned:' + without.map((s) => s.getID()).sort((a, b) => a - b).join(','),
      tone: 'warn',
      title: without.length + (without.length === 1 ? ' staff member without a badge' : ' staff members without a badge'),
      meta: listOf(without.map(name), 2),
      since: null,
      link: '/dashboard/people'
    });
  }

  return items;
}
